import type { PixelAccessor } from "../pixel-accessor";
import type { Color } from "../types";
import type { ColorInterpolator } from "./ColorInterpolator";

/**
 * Average color interpolation by 4, 9 or 16 nearest neighbors.
 */
export class AverageInterpolator implements ColorInterpolator {
  /**
   * AverageInterpolator constructor.
   *
   * @param neighborsCount Neighbors count per each dimension
   */
  constructor(private neighborsCount: 2 | 3 | 4) {
    if (![2, 3, 4].includes(this.neighborsCount)) {
      throw new RangeError(
        `Neighbors count must be 2, 3 or 4. ${this.neighborsCount} given`
      );
    }
  }

  /**
   * @inheritDoc
   */
  interpolate(image: PixelAccessor, x: number, y: number): Color {
    let startX, startY;
    const count = this.neighborsCount;

    switch (count) {
      case 2:
        startX = Math.floor(x);
        startY = Math.floor(y);
        break;

      case 3:
        startX = Math.floor(x + 0.5) - 1;
        startY = Math.floor(y + 0.5) - 1;
        break;
      case 4:
        startX = Math.floor(x) - 1;
        startY = Math.floor(y) - 1;
        break;
    }

    const endX = startX + count;
    const endY = startY + count;
    const color = [0, 0, 0, 0];

    for (let v = startY; v < endY; v++) {
      for (let u = startX; u < endX; u++) {
        const pixelColor = image.getPixelColor(u, v);
        color[0] += pixelColor[0];
        color[1] += pixelColor[1];
        color[2] += pixelColor[2];
        color[3] += pixelColor[3];
      }
    }

    const gamma = 1 / (count * count);

    return [
      Math.round(color[0] * gamma),
      Math.round(color[1] * gamma),
      Math.round(color[2] * gamma),
      Math.round(color[3] * gamma),
    ];
  }
}
